import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

import Section from "@/components/layout/Section";
import Container from "@/components/layout/Container";

const steps = [
  {
    title: "Initial consultation",
    text: "We understand your property, goals and potential.",
  },
  {
    title: "Property analysis",
    text: "Get a detailed revenue and demand performance report.",
  },
  {
    title: "Design and furnishing",
    text: "Interior planning and sourcing to maximise guest appeal.",
  },
  {
    title: "Listing and launch",
    text: "Photos, content and optimisation across platforms.",
  },
  {
    title: "Full management",
    text: "End-to-end Airbnb operations and continuous optimisation.",
  },
];

export default function SolutionsProcess() {
  const [active, setActive] = useState(0);
  const mobileRefs = useRef([]);

  useEffect(() => {
    const el = mobileRefs.current[active];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [active]);

  return (
    <Section background="bg-bg-light" padding="lg">
      <Container>

        {/* 🔥 HEADER (FIXED LEFT ALIGN) */}
        <div className="max-w-content mb-10 md:mb-14">

          <span className="text-small font-semibold uppercase tracking-[0.16em] text-text-muted">
            How it works
          </span>

          <h2 className="
            mt-2
            text-heading md:text-display
            font-extrabold text-text-primary
            leading-tight
            max-w-[18ch]
          ">
            A simple system to scale your property
          </h2>

          <p className="mt-3 text-body text-text-muted max-w-reading">
            From setup to optimisation, we manage everything end-to-end.
          </p>

        </div>

        {/* DESKTOP */}
        <div className="hidden md:block relative">

          <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border-soft" />

          <motion.div
            className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-accent-gold"
            animate={{ height: `${((active + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.35 }}
          />

          <div className="space-y-10">

            {steps.map((step, i) => {
              const isLeft = i % 2 === 0;
              const isActive = active === i;

              return (
                <div
                  key={i}
                  className="grid grid-cols-[minmax(0,1fr)_64px_minmax(0,1fr)] items-center"
                  onMouseEnter={() => setActive(i)}
                >

                  {/* LEFT */}
                  <div className={`${isLeft ? "pr-8 text-right" : ""}`}>
                    {isLeft && (
                      <Content step={step} i={i} isActive={isActive} />
                    )}
                  </div>

                  {/* CENTER */}
                  <div className="flex justify-center relative">

                    <div className={`
                      w-9 h-9 rounded-full flex items-center justify-center
                      transition-all duration-fast
                      ${isActive
                        ? "bg-accent-gold text-white shadow-soft"
                        : "bg-bg-surface border border-border-soft"}
                    `}>
                      <Check size={14} />
                    </div>

                  </div>

                  {/* RIGHT */}
                  <div className={`${!isLeft ? "pl-8 text-left" : ""}`}>
                    {!isLeft && (
                      <Content step={step} i={i} isActive={isActive} />
                    )}
                  </div>

                </div>
              );
            })}

          </div>

        </div>

        {/* 🔥 MOBILE (FIXED TYPOGRAPHY) */}
        <div className="md:hidden mt-6 space-y-3">

          {steps.map((step, i) => {
            const isActive = active === i;

            return (
              <div
                key={i}
                ref={(el) => (mobileRefs.current[i] = el)}
                onClick={() => setActive(i)}
                className={`
                  p-4 rounded-xl border transition-all duration-fast
                  ${isActive
                    ? "border-accent-gold bg-bg-surface shadow-soft"
                    : "border-border-soft bg-bg-surface"}
                `}
              >

                <div className="flex items-start gap-3">

                  <div className={`
                    w-7 h-7 rounded-md flex items-center justify-center text-[12px]
                    ${isActive
                      ? "bg-accent-gold text-white"
                      : "bg-bg-light text-text-muted"}
                  `}>
                    {i + 1}
                  </div>

                  <div className="flex-1">

                    <h3 className="text-body font-semibold text-text-primary leading-snug">
                      {step.title}
                    </h3>

                    <p className="text-small text-text-muted mt-1 leading-relaxed">
                      {step.text}
                    </p>

                  </div>

                </div>

              </div>
            );
          })}

        </div>

      </Container>
    </Section>
  );
}

/* CONTENT */

function Content({ step, i, isActive }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: isActive ? -2 : 0 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-[420px]"
    >
      <span className="text-micro text-text-muted">
        {String(i + 1).padStart(2, "0")}
      </span>

      <h3 className="mt-2 text-section font-semibold text-text-primary">
        {step.title}
      </h3>

      <p className="mt-2 text-body text-text-muted leading-relaxed">
        {step.text}
      </p>
    </motion.div>
  );
}